import React, { useCallback, useEffect, useState } from 'react';
import { Download, FileText, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { api } from '../../lib/api';
import { downloadFile, saveBlob } from '../../lib/api/billing';
import { euroAusCents } from '../../lib/api/standortAngebot';

type Lauf = {
  id: number;
  runNumber: string;
  art: string;
  direction: 'debit' | 'credit';
  entityName: string;
  date: string;
  itemCount: number;
  totalCents: number;
};

/**
 * Kontoauszüge zum Testen (T-0225): je eingereichtem Hausbank-Lauf ein camt.053,
 * das genau zu dessen Aufträgen passt. Nur mit freigeschalteten Testdaten.
 */
export default function TestBankdateienCard() {
  const [laeufe, setLaeufe] = useState<Lauf[] | null>(null);
  const [laedt, setLaedt] = useState<number | null>(null);

  const laden = useCallback(async () => {
    try {
      const antwort = await api.get<{ items: Lauf[] }>('/api/admin/e2e-seed/bankdateien');
      setLaeufe(antwort.items || []);
    } catch {
      setLaeufe(null);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  if (laeufe === null) return null;

  const herunterladen = async (lauf: Lauf) => {
    setLaedt(lauf.id);
    try {
      const { blob, filename } = await downloadFile(
        `/api/admin/e2e-seed/bankdateien/${lauf.id}`,
        `kontoauszug-test-${lauf.runNumber}.xml`,
      );
      saveBlob(blob, filename);
    } catch (fehler) {
      toast.error(fehler instanceof Error ? fehler.message : 'Download fehlgeschlagen');
    } finally {
      setLaedt(null);
    }
  };

  return (
    <Card className="border-dashed border-amber-400/70">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4 text-amber-500" />
          Kontoauszüge zum Testen
        </CardTitle>
        <p className="mt-1 text-sm text-muted-foreground">
          Zu jedem eingereichten Lastschrift- oder Erstattungslauf ein Kontoauszug (camt.053),
          der genau zu dessen Aufträgen passt. Beim Lastschriftlauf: Sammelgutschrift und eine
          Rücklastschrift (AM04, 3,50 € Gebühr). Bei Erstattungen: ausgeführte Überweisungen und
          ein Rückläufer (AC04). Einspielen unter Finanzen › Kontoauszüge.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {laeufe.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Noch kein eingereichter Lauf. Erst einen Lauf erstellen, freigeben und als
            eingereicht markieren, dann steht hier der passende Kontoauszug.
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {laeufe.map((lauf) => (
              <li key={lauf.id} className="flex flex-wrap items-center justify-between gap-2 p-2 text-sm">
                <span>
                  <span className="font-medium">{lauf.runNumber}</span> · {lauf.art} · {lauf.entityName} ·{' '}
                  {lauf.itemCount} Aufträge · {euroAusCents(lauf.totalCents)} €
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  disabled={laedt !== null}
                  onClick={() => void herunterladen(lauf)}
                >
                  {laedt === lauf.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                  Kontoauszug
                </Button>
              </li>
            ))}
          </ul>
        )}
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => void laden()}>
          <RefreshCw className="h-3.5 w-3.5" />
          Aktualisieren
        </Button>
      </CardContent>
    </Card>
  );
}
